import React, { useState } from "react";
import "./InputTextField.css";

export interface InputTextFieldProps {
  /**
   * Placeholder text for the input
   */
  placeholder?: string;

  /**
   * Current value of the input
   */
  value?: string;

  /**
   * Callback function when the value changes
   */
  onChange?: (value: string) => void;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Whether there is an error
   */
  error?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * Whether to show an icon in the input
   */
  showIcon?: boolean;

  /**
   * Additional CSS class
   */
  className?: string;
}

/**
 * Component for text input field with various states
 */
export const InputTextField: React.FC<InputTextFieldProps> = ({
  placeholder = "Placeholder",
  value = "",
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  showIcon = false,
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  const containerClasses = [
    "qida-input-textfield",
    disabled ? "qida-input-textfield--disabled" : "",
    error ? "qida-input-textfield--error" : "",
    isFocused ? "qida-input-textfield--focused" : "",
    isHovered ? "qida-input-textfield--hovered" : "",
    value ? "qida-input-textfield--filled" : "",
    showIcon ? "qida-input-textfield--with-icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      <div className="qida-input-textfield__container">
        <input
          type="text"
          className="qida-input-textfield__input"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={disabled}
        />
        {showIcon && (
          <div className="qida-input-textfield__icon">
            <svg
              width="15"
              height="8.5"
              viewBox="0 0 15 8.5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.5 1.5L7.5 7.5L13.5 1.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        {error && showIcon && (
          <div className="qida-input-textfield__icon-warning">
            <svg
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 0C4.701 0 0 4.701 0 10.5C0 16.299 4.701 21 10.5 21C16.299 21 21 16.299 21 10.5C21 4.701 16.299 0 10.5 0ZM10.5 16.1C9.849 16.1 9.324 15.575 9.324 14.924C9.324 14.273 9.849 13.748 10.5 13.748C11.151 13.748 11.676 14.273 11.676 14.924C11.676 15.575 11.151 16.1 10.5 16.1ZM11.676 11.593C11.676 12.244 11.151 12.769 10.5 12.769C9.849 12.769 9.324 12.244 9.324 11.593V4.984C9.324 4.333 9.849 3.808 10.5 3.808C11.151 3.808 11.676 4.333 11.676 4.984V11.593Z"
                fill="#A52222"
              />
            </svg>
          </div>
        )}
      </div>
      {error && errorMessage && (
        <div className="qida-input-textfield__error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default InputTextField;
